/* ==========================================================================
   sd-product.js - <sd-variant-picker>
   --------------------------------------------------------------------------
   Variant selection for the product page. Native Web Component, no framework,
   no dependencies.

   DOM CONTRACT the template must provide (templates/product.liquid matches it):

     <sd-variant-picker
        data-url="/products/handle"
        data-price-target="#SdPrice"
        data-sku-target="#SdSku"
        data-submit-target="#SdSubmit"
        data-id-input="#SdVariantId">
       <script type="application/json" data-sd-variants>[ ... ]</script>
       <fieldset data-sd-option-index="0"> ... <input type="radio" name="..." value="Black"> ... </fieldset>
       <div data-sd-live aria-live="polite" class="sd-u-visually-hidden"></div>
     </sd-variant-picker>

   The JSON payload is an array of:
     { id, available, options: [..], sku, featured_media_id }

   Note what is NOT in it: no price string and no HTML. Prices are pre-rendered
   per variant by sd-price.liquid into hidden nodes and this component only
   toggles which one is visible, so no markup is ever built from data at
   runtime.

   SINGLE-VARIANT PRODUCTS
   Most of this catalog is single-variant. The template renders no fieldsets at
   all in that case, and this component then does nothing beyond existing. It
   never synthesises a one-item selector, which is the usual way this pattern
   looks broken.

   NO JAVASCRIPT
   The template ships a real <select name="id"> inside <noscript>, so the form
   posts a valid variant id with scripting off. This component hides nothing and
   creates nothing that the form depends on.
   ========================================================================== */

class SdVariantPicker extends HTMLElement {
  #variants = [];
  #controller = null;
  #current = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#variants = this.#readVariants();
    if (!this.#variants.length) return;

    // A product whose only variant is "Default Title" has nothing to choose.
    // Bail out rather than rendering controls for a decision that does not exist.
    const fieldsets = this.querySelectorAll('[data-sd-option-index]');
    if (!fieldsets.length) return;

    this.addEventListener('change', this.#onChange.bind(this), { signal });

    this.#current = this.#matchSelected();
    this.#markUnavailable();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /* ---------------------------------------------------------------- data */

  #readVariants() {
    const node = this.querySelector('script[data-sd-variants]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // A malformed payload must not take the buy button down with it. The
      // form still posts whatever the id input already holds.
      console.warn('sd-variant-picker: could not parse variant data', error);
      return [];
    }
  }

  #selectedOptions() {
    const values = [];
    for (const fieldset of this.querySelectorAll('[data-sd-option-index]')) {
      const index = Number(fieldset.dataset.sdOptionIndex);
      const checked = fieldset.querySelector('input:checked, option:checked');
      values[index] = checked ? checked.value : null;
    }
    return values;
  }

  #matchSelected() {
    const wanted = this.#selectedOptions();
    for (const variant of this.#variants) {
      let hit = true;
      for (let i = 0; i < wanted.length; i++) {
        if (wanted[i] !== null && variant.options[i] !== wanted[i]) {
          hit = false;
          break;
        }
      }
      if (hit) return variant;
    }
    return null;
  }

  /* -------------------------------------------------------------- render */

  #onChange() {
    const variant = this.#matchSelected();
    this.#current = variant;

    this.#markUnavailable();
    this.#updateIdInput(variant);
    this.#updatePrice(variant);
    this.#updateSku(variant);
    this.#updateSubmit(variant);
    this.#updateMedia(variant);
    this.#updateUrl(variant);
    this.#announce(variant);

    this.dispatchEvent(
      new CustomEvent('sd:variant-change', { detail: { variant }, bubbles: true })
    );
  }

  #target(attr) {
    const selector = this.dataset[attr];
    return selector ? document.querySelector(selector) : null;
  }

  #updateIdInput(variant) {
    const input = this.#target('idInput');
    if (input && variant) input.value = variant.id;
  }

  /* The template pre-renders one price block PER VARIANT through
     sd-price.liquid and hides all but the current one. This component only
     flips `hidden`.

     It is deliberately not innerHTML with a price string from the JSON payload.
     Two reasons: the MAP guard and the compare-at rules stay in Liquid where
     they are already correct and tested, and there is no HTML-from-data path
     into the DOM at all, so this cannot become an injection sink later if the
     payload ever carries merchant text. */
  #updatePrice(variant) {
    const node = this.#target('priceTarget');
    if (!node) return;
    const wanted = variant ? String(variant.id) : '';
    for (const block of node.querySelectorAll('[data-sd-price-for]')) {
      block.hidden = block.dataset.sdPriceFor !== wanted;
    }
  }

  #updateSku(variant) {
    const node = this.#target('skuTarget');
    if (!node) return;
    const sku = variant && variant.sku ? variant.sku : '';
    node.textContent = sku;
    node.hidden = sku === '';
  }

  #updateSubmit(variant) {
    const button = this.#target('submitTarget');
    if (!button) return;

    if (!variant) {
      button.disabled = true;
      button.textContent = button.dataset.unavailableLabel || 'Unavailable';
      return;
    }
    if (!variant.available) {
      button.disabled = true;
      button.textContent = button.dataset.soldOutLabel || 'Sold out';
      return;
    }
    button.disabled = false;
    button.textContent = button.dataset.defaultLabel || 'Add to cart';
  }

  #updateMedia(variant) {
    if (!variant || !variant.featured_media_id) return;
    const gallery = document.querySelector('[data-sd-gallery]');
    if (!gallery) return;
    const thumb = gallery.querySelector(
      `[data-sd-media-id="${CSS.escape(String(variant.featured_media_id))}"]`
    );
    if (thumb && typeof thumb.click === 'function') thumb.click();
  }

  #updateUrl(variant) {
    if (!variant || !this.dataset.url) return;
    try {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    } catch {
      // A malformed data-url is not worth breaking selection over.
    }
  }

  /* Unavailable combinations are DISABLED and labelled, never removed. Removing
     them makes the option grid change shape while the shopper is using it. */
  #markUnavailable() {
    const chosen = this.#selectedOptions();

    for (const fieldset of this.querySelectorAll('[data-sd-option-index]')) {
      const index = Number(fieldset.dataset.sdOptionIndex);

      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        const probe = chosen.slice();
        probe[index] = input.value;

        const exists = this.#variants.some((v) =>
          probe.every((val, i) => val === null || v.options[i] === val)
        );
        const sellable = this.#variants.some(
          (v) =>
            v.available && probe.every((val, i) => val === null || v.options[i] === val)
        );

        const off = !exists || !sellable;
        input.disabled = !exists;
        input.setAttribute('aria-disabled', String(off));
        const label = input.closest('label') || fieldset.querySelector(`[for="${input.id}"]`);
        if (label) label.classList.toggle('sd-option--off', off);
      }
    }
  }

  /* Clear, wait a frame, then set. Assistive tech ignores a live region whose
     text is replaced in the same tick, so this is what makes it announce. */
  #announce(variant) {
    const region = this.querySelector('[data-sd-live]');
    if (!region) return;
    const template = region.dataset.template || '{{ variant }} selected';
    const title = variant ? variant.options.filter(Boolean).join(', ') : '';
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = title ? template.replace('{{ variant }}', title) : '';
    });
  }
}

if (!customElements.get('sd-variant-picker')) {
  customElements.define('sd-variant-picker', SdVariantPicker);
}

/* ==========================================================================
   Gallery.

   The markup is a scroll-snapped track that already works with nothing loaded:
   touch drag, trackpad swipe and the scrollbar are the browser's, not ours. This
   class adds what the platform does not give for free - arrows, a live counter,
   thumbnail syncing, keyboard control, and pausing a video the moment it is
   scrolled off screen - and marks the stage `is-enhanced` so the CSS can retire
   the scrollbar it no longer needs.

   Everything here degrades to "the track still scrolls".
   ========================================================================== */

class SdGallery extends HTMLElement {
  #controller = null;
  #stage = null;
  #frames = [];
  #thumbs = [];
  #index = 0;
  #observer = null;

  connectedCallback() {
    this.#stage = this.querySelector('[data-sd-gallery-stage]');
    if (!this.#stage) return;

    this.#frames = Array.from(this.#stage.querySelectorAll('[data-sd-media-frame]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-sd-media-id]'));
    if (this.#frames.length === 0) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#stage.classList.add('is-enhanced');

    for (const thumb of this.#thumbs) {
      thumb.addEventListener('click', this.#onThumb.bind(this), { signal });
    }

    const prev = this.querySelector('[data-sd-gallery-prev]');
    const next = this.querySelector('[data-sd-gallery-next]');
    const count = this.querySelector('[data-sd-gallery-count]');

    if (prev && next && this.#frames.length > 1) {
      prev.hidden = false;
      next.hidden = false;
      prev.addEventListener('click', () => this.#go(this.#index - 1), { signal });
      next.addEventListener('click', () => this.#go(this.#index + 1), { signal });
    }
    if (count && this.#frames.length > 1) count.hidden = false;

    this.#stage.addEventListener('keydown', this.#onKey.bind(this), { signal });

    /* Which frame is "current" is decided by what is actually centred in the track,
       not by what was last clicked - otherwise a swipe would leave the thumbnails and
       the counter describing a frame the visitor can no longer see. */
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = this.#frames.indexOf(entry.target);
          if (i >= 0 && i !== this.#index) this.#setCurrent(i);
        }
      },
      { root: this.#stage, threshold: 0.6 }
    );
    for (const frame of this.#frames) this.#observer.observe(frame);

    this.#setCurrent(0);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  #onThumb(event) {
    const thumb = event.currentTarget;
    const id = thumb.dataset.sdMediaId;
    const i = this.#frames.findIndex((f) => f.dataset.sdMediaFrame === id);
    if (i < 0) return;
    this.#go(i, this.#crossfade(i));

    /* Clicking a video thumbnail should start the video. Clicking a photo should not
       do anything beyond moving to it. */
    const frame = this.#frames[i];
    const video = frame && frame.querySelector('video');
    if (video && thumb.classList.contains('sd-pdp__thumb--play')) {
      const play = video.play();
      if (play && typeof play.catch === 'function') play.catch(() => {});
    }
  }

  #onKey(event) {
    if (event.key === 'ArrowRight') { event.preventDefault(); this.#go(this.#index + 1); }
    else if (event.key === 'ArrowLeft') { event.preventDefault(); this.#go(this.#index - 1); }
    else if (event.key === 'Home') { event.preventDefault(); this.#go(0); }
    else if (event.key === 'End') { event.preventDefault(); this.#go(this.#frames.length - 1); }
  }

  #go(i, instant) {
    const clamped = Math.max(0, Math.min(i, this.#frames.length - 1));
    const frame = this.#frames[clamped];
    if (!frame) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#stage.scrollTo({
      left: frame.offsetLeft - this.#stage.offsetLeft,
      behavior: reduce || instant ? 'auto' : 'smooth'
    });
    this.#setCurrent(clamped);
  }

  /* Thumbnail clicks crossfade: a copy of the outgoing image covers the viewport
     while the track jumps, then fades. Image-to-image, motion allowed only. */
  #crossfade(i) {
    if (!(window.sdMotionAllowed && window.sdMotionAllowed()) || i === this.#index) return false;
    const from = this.#frames[this.#index].querySelector('img');
    if (!from || !this.#frames[i].querySelector('img')) return false;

    this.querySelector('.sd-pdp__ghost')?.remove();
    const ghost = from.cloneNode(false);
    ghost.className = 'sd-pdp__ghost';
    this.#stage.after(ghost);
    /* A frame to paint it opaque, a frame to start the fade. */
    requestAnimationFrame(() => requestAnimationFrame(() => ghost.classList.add('is-gone')));
    setTimeout(() => ghost.remove(), 700);
    return true;
  }

  #setCurrent(i) {
    this.#index = i;

    for (const thumb of this.#thumbs) {
      const on = this.#frames[i] && thumb.dataset.sdMediaId === this.#frames[i].dataset.sdMediaFrame;
      thumb.setAttribute('aria-current', String(!!on));
      if (on && typeof thumb.scrollIntoView === 'function') {
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    const indexEl = this.querySelector('[data-sd-gallery-index]');
    if (indexEl) indexEl.textContent = String(i + 1);

    const prev = this.querySelector('[data-sd-gallery-prev]');
    const next = this.querySelector('[data-sd-gallery-next]');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= this.#frames.length - 1;

    /* A video left playing in a frame nobody is looking at is audio from nowhere. */
    for (let k = 0; k < this.#frames.length; k += 1) {
      if (k === i) continue;
      const v = this.#frames[k].querySelector('video');
      if (v && !v.paused) v.pause();
    }
  }
}

if (!customElements.get('sd-gallery')) {
  customElements.define('sd-gallery', SdGallery);
}

/* ==========================================================================
   Add-on configurator (snippets/sd-addons.liquid).

   ONE RULE GOVERNS THIS FILE: there must never be a moment where the visitor has
   ticked add-ons and a button on the page would add fewer things than they ticked.
   That is exactly the failure Tim reported about the launch bonus - a page that
   promised something the cart did not deliver. So the MAIN add-to-cart button is
   intercepted too, not just the one in this section, and a live note is injected
   under it saying what it is about to do.

   Degrades safely: with JS off the section still renders and reads as a catalogue of
   compatible parts, each linking nowhere but the tick boxes simply do nothing, and
   the ordinary add-to-cart still works exactly as before.
   ========================================================================== */

(function () {
  'use strict';

  function initAddons() {
    var root = document.querySelector('[data-sd-addons]');
    if (!root) return;

    var boxes = Array.prototype.slice.call(root.querySelectorAll('[data-sd-addon]'));
    if (!boxes.length) return;

    var bar = root.querySelector('[data-sd-addons-bar]');
    var countEl = root.querySelector('[data-sd-addons-count]');
    var totalEl = root.querySelector('[data-sd-addons-total]');
    var labelEl = root.querySelector('[data-sd-addons-label]');
    var submitEl = root.querySelector('[data-sd-addons-submit]');
    var clearEl = root.querySelector('[data-sd-addons-clear]');

    var form = document.getElementById('SdProductForm');
    var mainBtn = document.getElementById('SdSubmit');
    var currency = root.getAttribute('data-sd-currency') || 'USD';

    var fmt;
    try {
      fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: currency
      });
    } catch (e) {
      fmt = { format: function (n) { return '$' + n.toFixed(2); } };
    }
    function money(cents) { return fmt.format(cents / 100); }

    /* The note under the main button. Created here rather than in Liquid because it has
       nothing to say until something is ticked, and because the variant picker rewrites
       the button itself - this sits beside it and cannot be clobbered. */
    var note = null;
    function ensureNote() {
      if (note || !mainBtn || !mainBtn.parentNode) return note;
      note = document.createElement('p');
      note.className = 'sd-pdp__addnote';
      note.setAttribute('aria-live', 'polite');
      mainBtn.parentNode.insertBefore(note, mainBtn.nextSibling);
      return note;
    }

    function selected() {
      return boxes.filter(function (b) { return b.checked && !b.disabled; });
    }

    function render() {
      var picked = selected();
      var n = picked.length;
      var total = picked.reduce(function (sum, b) {
        return sum + (parseInt(b.getAttribute('data-price'), 10) || 0);
      }, 0);

      if (bar) bar.hidden = n === 0;
      if (countEl) countEl.textContent = n === 1 ? '1 add-on' : n + ' add-ons';
      if (totalEl) totalEl.textContent = n ? money(total) : '';
      if (labelEl) {
        labelEl.textContent = n
          ? 'Add rig + ' + n + (n === 1 ? ' add-on' : ' add-ons') + ' to cart'
          : 'Add to cart';
      }

      var el = ensureNote();
      if (el) {
        el.textContent = n
          ? 'Adds this rig plus ' + n + (n === 1 ? ' add-on' : ' add-ons') + ', ' + money(total) + ' more.'
          : '';
        el.hidden = n === 0;
      }
    }

    function items() {
      var qty = 1;
      if (form) {
        var q = form.querySelector('[name="quantity"]');
        if (q) qty = Math.max(1, parseInt(q.value, 10) || 1);
      }
      var id = form ? form.querySelector('[name="id"]') : null;
      var list = [];
      if (id && id.value) list.push({ id: Number(id.value), quantity: qty });
      selected().forEach(function (b) {
        list.push({ id: Number(b.value), quantity: 1 });
      });
      return list;
    }

    var busy = false;

    function addAll(trigger) {
      if (busy) return;
      var list = items();
      if (!list.length) return;

      busy = true;
      var restore = trigger ? trigger.textContent : '';
      if (trigger) { trigger.disabled = true; trigger.textContent = 'Adding'; }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: list })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || 'add failed'); });
          return res.json();
        })
        .then(function () {
          var n = selected().length;
          if (window.SDP && window.SDP.toast) {
            window.SDP.toast(n ? 'Rig and ' + n + (n === 1 ? ' add-on' : ' add-ons') + ' added to cart'
                               : 'Added to cart');
          }
          boxes.forEach(function (b) { b.checked = false; });
          render();
          if (window.SDP && window.SDP.syncCart) return window.SDP.syncCart();
        })
        .then(function () {
          if (typeof window.openAmpDrawer === 'function') window.openAmpDrawer();
        })
        .catch(function (err) {
          /* Never fail silently on the highest-value action on the site. */
          if (window.SDP && window.SDP.toast) {
            window.SDP.toast('Could not add everything to the cart. Please try again.');
          }
          if (window.console) window.console.warn('[sd-addons]', err);
        })
        .finally(function () {
          busy = false;
          if (trigger) { trigger.disabled = false; trigger.textContent = restore; }
        });
    }

    boxes.forEach(function (b) { b.addEventListener('change', render); });

    if (submitEl) {
      submitEl.addEventListener('click', function () { addAll(submitEl); });
    }
    if (clearEl) {
      clearEl.addEventListener('click', function () {
        boxes.forEach(function (b) { b.checked = false; });
        render();
      });
    }

    /* The main button. Only intercepted when something is actually ticked, so the
       ordinary single-product path keeps whatever behaviour it already had, including
       the cart-drawer app's own handling of a native form post. */
    if (form) {
      form.addEventListener('submit', function (e) {
        if (!selected().length) return;
        e.preventDefault();
        e.stopPropagation();
        addAll(mainBtn);
      }, true);
    }

    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAddons);
  } else {
    initAddons();
  }
})();

/* ==========================================================================
   Buy column height ([data-sd-pdp-columns] in templates/product.liquid).

   The column is sticky so Add to cart stays in view. Now that the add-on rows
   live inside it, a rig with six groups makes the column taller than the
   viewport, and a sticky element taller than the viewport pins its top and
   traps everything under the fold until the gallery runs out. So the column is
   measured, and while it does not fit, `.is-tall` hands the sticky role to the
   gallery instead (sd-pages.css). Pure enhancement: without this the column
   simply sticks the way it always did.
   ========================================================================== */

(function () {
  'use strict';

  function initColumn() {
    var grid = document.querySelector('[data-sd-pdp-columns]');
    var buy = grid && grid.querySelector('[data-sd-pdp-buy]');
    if (!buy) return;

    var wide = window.matchMedia('(min-width: 990px)');

    /* 92px is the sticky offset in sd-pages.css. The rest is breathing room so
       a column that fits by two pixels is not treated as fitting. */
    function check() {
      var room = window.innerHeight - 92 - 24;
      grid.classList.toggle('is-tall', wide.matches && buy.offsetHeight > room);
    }

    if ('ResizeObserver' in window) new ResizeObserver(check).observe(buy);
    window.addEventListener('resize', check, { passive: true });
    check();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColumn);
  } else {
    initColumn();
  }
})();

/* ==========================================================================
   Description layout ([data-sd-desc] in templates/product.liquid).

   Supplier descriptions arrive as one long column of HTML: a few paragraphs,
   then headed sections - what's in the box, specifications, compatibility -
   most of which are lists or tables. Tim: "short in width and very long in
   length ... use all space that you have". The CSS already flows the text in
   two columns; this goes one better where the HTML allows it, lifting each
   headed section that holds a list or a table out of the prose into a column
   of cards beside it, so the page reads as prose on the left and facts on the
   right.

   WHAT KEEPS THIS SAFE ON 640 DESCRIPTIONS IT HAS NEVER SEEN
   - Nothing is built from text. Nodes are MOVED, never rebuilt, so the
     supplier's markup (links, bold, tables) survives exactly as it came.
   - A description with no headings, or with bare text outside any element,
     is left exactly as it is: the CSS columns handle it.
   - Sections split at the most frequent heading level, not the highest, so a
     single <h2> title over <h3> sections does not swallow the whole thing.
   - A headed section goes right only if it holds a list or a table. Prose
     sections (FAQ, shipping notes) stay left in their original order.
   - If the prose left over is too short to hold up a column, the cards tile
     two-up under it (.is-cards) rather than beside a mostly empty column.
   ========================================================================== */

(function () {
  'use strict';

  var BLOCK = 'ul, ol, table';

  function textLength(nodes) {
    return nodes.reduce(function (n, el) {
      return n + (el.textContent || '').trim().length;
    }, 0);
  }

  function holdsBlock(el) {
    return el.matches(BLOCK) || !!el.querySelector(BLOCK);
  }

  function initDescription() {
    var desc = document.querySelector('[data-sd-desc]');
    var rte = desc && desc.querySelector('[data-sd-desc-body]');
    if (!rte) return;

    /* Some editors wrap the whole description in a single <div>. Look through it. */
    var host = rte;
    while (
      host.children.length === 1 &&
      /^(DIV|SECTION|ARTICLE)$/.test(host.firstElementChild.tagName) &&
      host.firstElementChild.children.length > 1
    ) {
      host = host.firstElementChild;
    }

    /* Bare text between elements would be lost by a move. Leave such a description alone. */
    for (var c = host.firstChild; c; c = c.nextSibling) {
      if (c.nodeType === 3 && c.textContent.trim()) return;
    }

    var nodes = Array.prototype.slice.call(host.children);
    var counts = {};
    nodes.forEach(function (el) {
      if (/^H[1-6]$/.test(el.tagName)) counts[el.tagName] = (counts[el.tagName] || 0) + 1;
    });
    var levels = Object.keys(counts).sort(function (a, b) {
      return counts[b] - counts[a] || a.localeCompare(b);
    });
    if (!levels.length) return;
    var level = levels[0];

    var sections = [];
    var current = { head: null, body: [] };
    nodes.forEach(function (el) {
      if (el.tagName === level) {
        sections.push(current);
        current = { head: el, body: [] };
      } else {
        current.body.push(el);
      }
    });
    sections.push(current);

    var prose = [];
    var cards = [];
    sections.forEach(function (s) {
      if (s.head && s.body.some(holdsBlock)) {
        cards.push(s);
      } else {
        if (s.head) prose.push(s.head);
        prose.push.apply(prose, s.body);
      }
    });
    if (!cards.length) return;

    var proseEl = document.createElement('div');
    proseEl.className = 'sd-pdp__prose';
    prose.forEach(function (el) { proseEl.appendChild(el); });

    var cardsEl = document.createElement('div');
    cardsEl.className = 'sd-pdp__cards';
    cards.forEach(function (s) {
      var card = document.createElement('section');
      card.className = 'sd-pdp__card';
      card.appendChild(s.head);
      s.body.forEach(function (el) { card.appendChild(el); });
      cardsEl.appendChild(card);
    });

    /* Every element has been moved out; what is left in the host is whitespace
       or the wrapper itself. */
    rte.textContent = '';
    if (prose.length) rte.appendChild(proseEl);
    rte.appendChild(cardsEl);
    rte.classList.add(textLength(prose) >= 600 ? 'is-split' : 'is-cards');
  }

  /* The Read more clamp is CSS. This only retires the button when the text
     turns out to fit inside the clamp anyway, which a 950-character description
     in two columns does - a button that reveals nothing is worse than none.
     Re-checked on resize because the column count changes at 990px. */
  function initReadMore() {
    var desc = document.querySelector('[data-sd-desc]');
    if (!desc || !desc.classList.contains('is-long')) return;
    var rte = desc.querySelector('[data-sd-desc-body]');
    var more = desc.querySelector('[data-sd-desc-more]');
    if (!rte || !more) return;

    function check() {
      if (more.open || rte.clientHeight === 0) return;
      desc.classList.remove('is-fits');
      /* Multicol overflow spills sideways into extra columns, so both axes count. */
      var fits =
        rte.scrollHeight <= rte.clientHeight + 4 &&
        rte.scrollWidth <= rte.clientWidth + 4;
      desc.classList.toggle('is-fits', fits);
    }

    check();
    window.addEventListener('resize', check, { passive: true });
  }

  function init() {
    initDescription();
    initReadMore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
